"use client";
import { Colors } from "@/enums/Colors";
import { useResponsive } from "@/hooks/useResponsive";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { IconButton, Snackbar, styled } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useEffect, useState } from "react";

interface SnackBarData {
  text: string;
  severity: "error" | "warning" | "info" | "success";
}

/**
 * Shows a Snackbar message on top of the screen.
 *
 * Usage: dispatch an event with the following structure in any component.
 *
 *  const sendMessage = (data: SnackBarData) => {
 *    const snackData = data;
 *    const customEvent = new CustomEvent('snackMessage', {detail: {snackData}});
 *    document.dispatchEvent(customEvent);
 *  }
 *
 *   This SnackbarComponent with listen to that event and update the snackbar properly.
 */
function LazoSnackBarComponent() {
  const [snackData, setSnackData] = useState<SnackBarData | null>(null);
  const { isMobile } = useResponsive();

  useEffect(() => {
    document.addEventListener("snackMessage", updateSnackData);
    return () => {
      document.removeEventListener("snackMessage", updateSnackData);
    };
  }, []);

  const updateSnackData = (event: any) => {
    setSnackData(event.detail.snackData);
  };

  const getIcon = () => {
    const baseStyle = {
      height: { xs: "20px", sm: "24px" },
      width: { xs: "20px", sm: "24px" },
      marginRight: { xs: "-4px", sm: "-5px" },
    };
    switch (snackData.severity) {
      case "success":
        return (
          <CheckIcon
            sx={{
              color: Colors.ACCENT_GREEN_01A902,
              ...baseStyle,
            }}
          />
        );
      case "warning":
        return (
          <WarningAmberIcon
            sx={{
              color: Colors.ORANGE_FF9A3D,
              ...baseStyle,
            }}
          />
        );
      case "error":
        return (
          <InfoIcon
            sx={{
              color: Colors.HEART_RED,
              ...baseStyle,
            }}
          />
        );
      default:
        return (
          <InfoIcon
            sx={{
              color: Colors.LAZO_PURPLE,
              ...baseStyle,
            }}
          />
        );
    }
  };

  const closeSnack = () => setSnackData(null);
  const hideDuration = 3000;

  return (
    snackData && (
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isMobile ? "center" : "right",
        }}
        sx={{
          margin: {
            xs: "0 16px 16px 16px", // Mobile: centered with side margins
            sm: "0 24px 24px 0", // Desktop: bottom-right positioning
          },
        }}
        open={!!snackData}
        autoHideDuration={hideDuration}
        onClose={closeSnack}
      >
        <ResponsiveStyledMuiAlert elevation={6} variant="filled" icon={getIcon()}>
          {snackData.text}
          <IconButton
            sx={{
              position: "absolute",
              right: { xs: "12px", sm: "16px" },
              top: "50%",
              transform: "translateY(-50%)",
              minWidth: { xs: "32px", sm: "40px" },
              minHeight: { xs: "32px", sm: "40px" },
              padding: { xs: "4px", sm: "8px" },
            }}
            onClick={closeSnack}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: "18px", sm: "20px" } }} />
          </IconButton>
        </ResponsiveStyledMuiAlert>
      </Snackbar>
    )
  );
}

const ResponsiveStyledMuiAlert = styled(MuiAlert)(({ theme }) => ({
  backgroundColor: Colors.WHITE,
  borderRadius: "16px",
  color: Colors.BLACK_2,
  display: "flex",
  alignItems: "center",
  boxShadow: "0px 0px 15px 0px #00000033",
  fontWeight: 600,
  position: "relative",
  wordBreak: "break-word",
  // Responsive styling
  [theme.breakpoints.down("sm")]: {
    padding: "12px 44px 12px 12px", // Less padding on mobile, account for smaller close button
    minWidth: "280px",
    maxWidth: "calc(100vw - 32px)", // Ensure it fits within viewport with margins
    fontSize: "13px",
    minHeight: "48px", // Ensure adequate touch target height
    maxHeight: "none", // Allow text wrapping on mobile
    lineHeight: "1.4",
  },
  [theme.breakpoints.up("sm")]: {
    padding: "16px 50px 16px 16px",
    minWidth: "320px",
    maxWidth: "450px",
    fontSize: "14px",
    minHeight: "56px",
    maxHeight: "none",
    lineHeight: "1.3",
  },
}));

export default LazoSnackBarComponent;
